import { Alert, Drawer, Empty, Flex, Skeleton, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../../constants/routes.ts"
import type {
  StatusAutomationPreview,
  StatusAutomationPreviewRecord
} from "../../../api/status-automation-settings.services.ts"
import { ruleCopyKey, targetStatusLabel } from "./status-automation-form.ts"

const { Text } = Typography

const P = "settingsConfiguration.statusAutomation.preview"

export type PreviewState =
  | { status: "closed" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; preview: StatusAutomationPreview }

const recordPath = (record: StatusAutomationPreviewRecord) =>
  record.is_project
    ? RoutesPaths.project.view(record.id).overview
    : RoutesPaths.lead.view(record.id)

interface Props {
  state: PreviewState
  onClose: () => void
}

export function StatusAutomationPreviewDrawer({ state, onClose }: Props) {
  const { t } = useTranslation()

  const body = () => {
    if (state.status === "loading")
      return <Skeleton active paragraph={{ rows: 4 }} />
    if (state.status === "error")
      return <Alert type="error" showIcon message={t(`${P}.error`)} />
    if (state.status !== "loaded") return null

    const moves = state.preview.moves.filter((move) => move.count > 0)
    return (
      <Flex vertical gap={16}>
        <Text>{t(`${P}.intro`, { count: state.preview.window_days })}</Text>
        {moves.length === 0 ? (
          <Empty description={t(`${P}.none`)} />
        ) : (
          moves.map((move) => (
            <section
              key={move.rule}
              aria-label={t(`${ruleCopyKey(move.rule)}.name`, {
                defaultValue: move.rule
              })}
              className="flex flex-col gap-1"
            >
              <Text strong>
                {targetStatusLabel(move.rule, move.target) ??
                  t(`${ruleCopyKey(move.rule)}.name`, {
                    defaultValue: move.rule
                  })}
                {": "}
                {t(`${P}.move`, { count: move.count })}
              </Text>
              <ul className="m-0 pl-5">
                {move.records.map((record) => (
                  <li key={`${record.is_project}:${record.id}`}>
                    <Link to={recordPath(record)}>
                      {record.uid} · {record.name}
                    </Link>{" "}
                    <Tag bordered={false}>
                      {record.is_project ? t(`${P}.project`) : t(`${P}.lead`)}
                    </Tag>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
        <Text type="secondary">{t(`${P}.readOnlyNote`)}</Text>
      </Flex>
    )
  }

  return (
    <Drawer
      open={state.status !== "closed"}
      onClose={onClose}
      title={t(`${P}.title`)}
      width={520}
      destroyOnHidden
    >
      {body()}
    </Drawer>
  )
}
